import React, { useId, useMemo } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled, { useTheme } from "styled-components";

export type MetricChartKind = "area" | "bar" | "line";

export interface MetricChartPoint {
  label: string;
  value: number;
}

interface MetricChartProps {
  data: MetricChartPoint[];
  kind: MetricChartKind;
  expanded?: boolean;
  showAverage?: boolean;
  formatValue: (value: number) => string;
  formatAxisValue?: (value: number) => string;
  ariaLabel?: string;
}

const ChartSurface = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;

  .recharts-surface {
    overflow: visible;
  }
`;

const MetricChart: React.FC<MetricChartProps> = ({
  data,
  kind,
  expanded = false,
  showAverage = false,
  formatValue,
  formatAxisValue = formatValue,
  ariaLabel = "График статистики",
}) => {
  const theme = useTheme();
  const rawGradientId = useId();
  const gradientId = `metric-gradient-${rawGradientId.replace(/:/g, "")}`;
  const average = useMemo(
    () => data.length ? data.reduce((sum, point) => sum + point.value, 0) / data.length : 0,
    [data],
  );
  const xInterval: number | "preserveStartEnd" = expanded
    ? "preserveStartEnd"
    : Math.max(0, Math.floor(data.length / 5) - 1);

  return (
    <ChartSurface role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: expanded ? 20 : 12, right: 8, bottom: 4, left: 4 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.colors.brand} stopOpacity={0.2} />
              <stop offset="100%" stopColor={theme.colors.brand} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={theme.borders.divider} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            interval={xInterval}
            tick={{ fill: theme.colors.inkMuted, fontSize: expanded ? 12 : 11 }}
            minTickGap={12}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: number) => formatAxisValue(value)}
            tick={{ fill: theme.colors.inkMuted, fontSize: expanded ? 12 : 11 }}
            width={expanded ? 70 : 58}
            tickCount={expanded ? 6 : 4}
          />
          <Tooltip
            formatter={(value) => formatValue(Number(value))}
            contentStyle={{
              border: `1px solid ${theme.borders.outline}`,
              borderRadius: 10,
              background: theme.colors.white,
              color: theme.colors.ink,
              boxShadow: "0 4px 16px rgba(31, 10, 53, 0.10)",
              fontSize: 13,
              padding: "9px 12px",
            }}
            labelStyle={{ color: theme.colors.inkSoft, marginBottom: 4 }}
            cursor={kind === "bar"
              ? { fill: theme.colors.brandSubtle }
              : { stroke: theme.colors.brandStrong, strokeWidth: 1 }}
          />
          {expanded && showAverage && data.length > 0 && (
            <ReferenceLine
              y={average}
              stroke={theme.colors.inkMuted}
              strokeDasharray="5 5"
              strokeWidth={1}
              label={{
                value: "среднее",
                fill: theme.colors.inkMuted,
                fontSize: 11,
                position: "insideTopRight",
              }}
            />
          )}
          {kind === "bar" && (
            <Bar
              dataKey="value"
              fill={theme.colors.brandGlow}
              maxBarSize={expanded ? 34 : 16}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
            />
          )}
          {kind === "line" && (
            <Line
              type="linear"
              dataKey="value"
              stroke={theme.colors.brand}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: theme.colors.brand }}
              isAnimationActive={false}
            />
          )}
          {kind === "area" && (
            <Area
              type="linear"
              dataKey="value"
              stroke={theme.colors.brand}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4, fill: theme.colors.brand }}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartSurface>
  );
};

export default MetricChart;
